"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNodeProviderConnected } from "@/actions/workflows/actions";
import IntegrationTile from "@/components/integrations/marketplace/IntegrationTile";
import Icon from "@/components/library/Icon";
import { JOTFORM_STORED_PROVIDER } from "@/types/jotform";
import { unwrapSessionExpiry } from "@/utils/common/sessionExpiry";
import JotFormSetupPanel from "./JotFormSetupPanel";

type Status = "loading" | "connected" | "disconnected";

export default function JotFormIntegrationCard() {
  const { t } = useTranslation("partners");
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    // The stored provider string, not the `jotform` route segment — the generic
    // route matches on the credential row's provider column. The helper filters
    // on `status === "active"`, so a revoked-but-present row reads as
    // disconnected rather than offering a Manage dialog over a dead credential.
    // `.catch(() => false)` mirrors the sibling Google card: never crash the grid.
    // An expired session comes back as a marker rather than a throw; unwrapping
    // it raises the session modal and reads as "not connected" here.
    const connected = unwrapSessionExpiry(
      await isNodeProviderConnected(JOTFORM_STORED_PROVIDER).catch(() => false),
      false
    );
    setStatus(connected ? "connected" : "disconnected");
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connected = status === "connected";

  return (
    <IntegrationTile
      testId="jotform-integration-card"
      closeTestId="jotform-setup-close"
      icon={
        <Icon icon="clipboard-list" className="w-5 h-5 text-orange-600 dark:text-orange-400" />
      }
      iconClassName="bg-orange-100 dark:bg-orange-900"
      title={t("jotformIntegration.title")}
      connected={connected}
      actionDisabled={status === "loading"}
      actionLabel={
        connected ? t("jotformIntegration.manage") : t("jotformIntegration.connect")
      }
      dialogTitle={t("jotformIntegration.dialogTitle")}
      dialogDescription={t("jotformIntegration.dialogDescription")}
      description={
        status === "loading" ? (
          <p className="text-600 text-sm">{t("jotformIntegration.loading")}</p>
        ) : (
          <p className="text-sm text-600">
            {connected
              ? t("jotformIntegration.connectedDescription")
              : t("jotformIntegration.disconnectedDescription")}
          </p>
        )
      }
    >
      {/*
        The panel unmounts with the dialog, which drops the revealed secret from
        hook state exactly as the callout's dismiss does.
      */}
      <JotFormSetupPanel onChanged={refresh} />
    </IntegrationTile>
  );
}
